import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { calculatePricing, formatEmailCount, createQuoteData, getPricingSlabs } from '../../utils/pricingCalculator';
import { PricingSlab } from '../../types';
import PricingTable from './PricingTable';
import PriceResults from './PriceResults';
import QuoteModal from './QuoteModal';
import { useUser } from '../../contexts/UserContext';
import './PriceCalculator.css';

const PriceCalculator: React.FC = () => {
  const [emailCount, setEmailCount] = useState<string>('');
  const [currency, setCurrency] = useState<'INR' | 'USD'>('INR');
  const [showPricingTable, setShowPricingTable] = useState(false);
  const [customerName, setCustomerName] = useState<string>('');
  const [companyName, setCompanyName] = useState<string>('');
  const [discountPercentage, setDiscountPercentage] = useState<number>(0);
  const [discountInputValue, setDiscountInputValue] = useState<string>('0');
  const [discountReason, setDiscountReason] = useState<string>('');
  const [showQuoteModal, setShowQuoteModal] = useState(false);
  const [pricingSlabs, setPricingSlabs] = useState<{ INR: PricingSlab[], USD: PricingSlab[] }>({ INR: [], USD: [] });
  const [isLoadingPricing, setIsLoadingPricing] = useState(true);
  const [pricingError, setPricingError] = useState<string | null>(null);
  const [pricingResult, setPricingResult] = useState<any>(null);
  const { user } = useUser();

  const emailCountNumber = useMemo(() => {
    const num = parseInt(emailCount.replace(/,/g, ''), 10);
    return isNaN(num) ? 0 : num;
  }, [emailCount]);

  // Load pricing slabs on component mount
  useEffect(() => {
    const loadPricingSlabs = async () => {
      setIsLoadingPricing(true);
      setPricingError(null);
      try {
        const [inrSlabs, usdSlabs] = await Promise.all([
          getPricingSlabs('INR'),
          getPricingSlabs('USD')
        ]);
        setPricingSlabs({ INR: inrSlabs, USD: usdSlabs });
      } catch (error) {
        setPricingError('Unable to load pricing data. Please check your connection.');
        console.error('Failed to load pricing slabs:', error);
      } finally {
        setIsLoadingPricing(false);
      }
    };

    loadPricingSlabs();
  }, []);

  // Calculate pricing when email count, currency, or discount changes
  useEffect(() => {
    const calculatePricingAsync = async () => {
      if (emailCountNumber > 0 && !isLoadingPricing) {
        try {
          const result = await calculatePricing(emailCountNumber, currency, discountPercentage);
          setPricingResult(result);
        } catch (error) {
          console.error('Failed to calculate pricing:', error);
          setPricingResult(null);
        }
      } else {
        setPricingResult(null);
      }
    };

    calculatePricingAsync();
  }, [emailCountNumber, currency, discountPercentage, isLoadingPricing]);

  const handleEmailCountChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/[^\d]/g, ''); // Only allow digits
    if (value === '') {
      setEmailCount('');
      return;
    }
    
    const num = parseInt(value, 10);
    if (num > 1000000000) return; // Max 100 crore emails
    
    // Format with commas
    const formatted = new Intl.NumberFormat('en-IN').format(num);
    setEmailCount(formatted);
  }, []);

  const handleCurrencyToggle = useCallback(() => {
    setCurrency(prev => prev === 'INR' ? 'USD' : 'INR');
  }, []);

  const handlePresetClick = useCallback((count: number) => {
    const formatted = new Intl.NumberFormat('en-IN').format(count);
    setEmailCount(formatted);
  }, []);

  const handleDiscountPercentageChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    
    // Always update the display value for immediate feedback
    setDiscountInputValue(value);
    
    // Handle empty value
    if (value === '' || value === '0') {
      setDiscountPercentage(0);
      return;
    }
    
    // Validate and update the actual discount percentage used in calculations
    const numValue = parseFloat(value);
    if (!isNaN(numValue) && numValue >= 0 && numValue <= 50) {
      setDiscountPercentage(numValue);
    }
  }, []);

  const handleDiscountInputBlur = useCallback(() => {
    // On blur, ensure the input shows the actual discount percentage value
    const displayValue = discountPercentage === 0 ? '0' : discountPercentage.toString();
    setDiscountInputValue(displayValue);
  }, [discountPercentage]);

  const handleDiscountReasonChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    setDiscountReason(e.target.value);
  }, []);

  const handleGenerateQuote = useCallback(() => {
    if (emailCountNumber > 0) {
      setShowQuoteModal(true);
    }
  }, [emailCountNumber]);

  const quoteData = useMemo(() => {
    if (emailCountNumber > 0 && pricingResult) {
      return createQuoteData(
        customerName || 'Valued Customer',
        companyName || 'Your Company',
        emailCountNumber,
        pricingResult,
        discountReason || undefined,
        user ? { name: user.name || 'Sales Team', email: user.email || 'sales@mailercloud.com' } : undefined
      );
    }
    return null;
  }, [customerName, companyName, emailCountNumber, pricingResult, discountReason, user]);

  const presetValues = [50000, 100000, 300000, 1000000, 5000000, 10000000]; // 50K, 1L, 3L, 10L, 50L, 1cr

  // Show loading state
  if (isLoadingPricing) {
    return (
      <div className="price-calculator" role="main" aria-label="Marketing Emails Price Calculator">
        <div className="calculator-container">
          <div className="calculator-header">
            <h1>Marketing Emails Price Calculator</h1>
            <p>Loading pricing data...</p>
          </div>
          <div className="calculator-card">
            <div className="loading-spinner">Loading...</div>
          </div>
        </div>
      </div>
    );
  }

  // Show error state
  if (pricingError) {
    return (
      <div className="price-calculator" role="main" aria-label="Marketing Emails Price Calculator">
        <div className="calculator-container">
          <div className="calculator-header">
            <h1>Marketing Emails Price Calculator</h1>
          </div>
          <div className="calculator-card">
            <div className="error-message">
              <p>{pricingError}</p>
              <button onClick={() => window.location.reload()} className="retry-btn">
                Retry
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="price-calculator" role="main" aria-label="Marketing Emails Price Calculator">
      <div className="calculator-container">
        {/* Header */}
        <div className="calculator-header">
          <h1>Marketing Emails Price Calculator</h1>
          <p>Calculate your monthly cost based on email volume</p>
        </div>

        {/* Main Calculator Card */}
        <div className="calculator-card">
          <div className="calculator-form">
            {/* Email Count and Currency Row */}
            <div className="compact-row">
              <div className="input-group email-count-group">
                <label htmlFor="email-count" className="input-label">
                  Number of Emails per Month
                </label>
                <div className="input-wrapper">
                  <input
                    id="email-count"
                    type="text"
                    value={emailCount}
                    onChange={handleEmailCountChange}
                    placeholder="Enter email count"
                    className="email-input"
                    aria-describedby="email-count-help"
                  />
                  <div className="input-suffix">emails/month</div>
                </div>
                <div id="email-count-help" className="input-help">
                  Enter the number of emails you plan to send monthly
                </div>
              </div>

              {/* Currency Toggle */}
              <div className="currency-toggle compact">
                <span className="currency-label">Currency:</span>
                <button
                  onClick={handleCurrencyToggle}
                  className="currency-btn"
                  type="button"
                  aria-label={`Switch to ${currency === 'INR' ? 'USD' : 'INR'}`}
                >
                  <span className={`currency-option ${currency === 'INR' ? 'active' : ''}`}>₹ INR</span>
                  <span className="currency-divider">|</span>
                  <span className={`currency-option ${currency === 'USD' ? 'active' : ''}`}>$ USD</span>
                </button>
              </div>
            </div>

            {/* Discount Section */}
            <div className="discount-section">
              <h3 className="section-title">Discount Options</h3>
              <p className="section-help">
                Apply discounts to email costs for special pricing arrangements.
              </p>
              
              <div className="discount-fields-grid">
                <div className="input-group compact">
                  <label htmlFor="discount-percentage" className="input-label">
                    Discount Percentage (0-50%)
                  </label>
                  <div className="input-wrapper">
                    <input
                      id="discount-percentage"
                      type="number"
                      min="0"
                      max="50"
                      step="0.1"
                      value={discountInputValue}
                      onChange={handleDiscountPercentageChange}
                      onBlur={handleDiscountInputBlur}
                      placeholder="0"
                      className="discount-input"
                    />
                    <div className="input-suffix">%</div>
                  </div>
                  <div className="input-help">
                    Maximum 50% discount allowed on email costs
                  </div>
                </div>

                <div className="input-group compact">
                  <label htmlFor="discount-reason" className="input-label">
                    Discount Reason
                  </label>
                  <select
                    id="discount-reason"
                    value={discountReason}
                    onChange={handleDiscountReasonChange}
                    className="discount-select"
                  >
                    <option value="">Select reason</option>
                    <option value="volume_discount">Volume Discount</option>
                    <option value="enterprise_plan">Enterprise Plan</option>
                    <option value="partnership">Partnership Agreement</option>
                    <option value="promotional">Promotional Offer</option>
                    <option value="loyalty_discount">Loyalty Discount</option>
                    <option value="custom">Custom Agreement</option>
                  </select>
                  <div className="input-help">
                    Select the reason for applying discount
                  </div>
                </div>
              </div>
            </div>

            {/* Customer Information - Compact 2-Column */}
            <div className="customer-info-compact">
              <div className="customer-fields-grid">
                <div className="input-group compact">
                  <label htmlFor="customer-name" className="input-label">
                    Customer Name
                  </label>
                  <input
                    id="customer-name"
                    type="text"
                    value={customerName}
                    onChange={(e) => setCustomerName(e.target.value)}
                    placeholder="Customer name"
                    className="customer-input"
                  />
                </div>

                <div className="input-group compact">
                  <label htmlFor="company-name" className="input-label">
                    Company Name
                  </label>
                  <input
                    id="company-name"
                    type="text"
                    value={companyName}
                    onChange={(e) => setCompanyName(e.target.value)}
                    placeholder="Company name"
                    className="customer-input"
                  />
                </div>
              </div>
            </div>

            {/* Preset Buttons */}
            <div className="preset-buttons">
              <span className="preset-label">Quick Select:</span>
              {presetValues.map(value => (
                <button
                  key={value}
                  onClick={() => handlePresetClick(value)}
                  className={`preset-btn ${emailCountNumber === value ? 'active' : ''}`}
                  type="button"
                >
                  {formatEmailCount(value)}
                </button>
              ))}
            </div>
          </div>

          {/* Results */}
          {emailCountNumber > 0 && pricingResult && (
            <PriceResults 
              result={pricingResult}
              emailCount={emailCountNumber}
            />
          )}

          {/* Generate Quote Button */}
          {emailCountNumber > 0 && pricingResult && (
            <div className="generate-quote-section">
              <button
                onClick={handleGenerateQuote}
                className="generate-quote-btn"
                type="button"
              >
                <span className="quote-btn-icon">📄</span>
                Generate Professional Quote
              </button>
              <p className="quote-help-text">
                Create a professional quotation with Mailercloud branding for your client presentation
              </p>
            </div>
          )}
        </div>

        {/* Pricing Table Toggle */}
        <div className="table-toggle-section">
          <button
            onClick={() => setShowPricingTable(!showPricingTable)}
            className="table-toggle-btn"
            type="button"
            aria-expanded={showPricingTable}
          >
            {showPricingTable ? 'Hide' : 'View'} Pricing Table
            <span className={`toggle-icon ${showPricingTable ? 'expanded' : ''}`}>
              ▼
            </span>
          </button>
        </div>

        {/* Pricing Table */}
        {showPricingTable && (
          <PricingTable 
            currency={currency}
            slabs={currency === 'USD' ? pricingSlabs.USD : pricingSlabs.INR}
          />
        )}

        {/* Info Section */}
        <div className="info-section">
          <div className="info-card">
            <h3>How It Works</h3>
            <ul>
              <li>Enter your monthly email volume</li>
              <li>Pricing is calculated using tiered slabs</li>
              <li>Lower rates apply as volume increases</li>
              <li>First 1,000 emails are always free</li>
            </ul>
          </div>
          
          <div className="info-card">
            <h3>Features Included</h3>
            <ul>
              <li>Advanced email editor</li>
              <li>Automation workflows</li>
              <li>Detailed analytics</li>
              <li>24/7 customer support</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Quote Modal */}
      {quoteData && (
        <QuoteModal
          isOpen={showQuoteModal}
          onClose={() => setShowQuoteModal(false)}
          quoteData={quoteData}
        />
      )}
    </div>
  );
};

export default PriceCalculator;